import React, { useRef, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Droppable } from "react-beautiful-dnd";
import { Tooltip } from "@mui/material";

import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import KanbanCard from "../../../../../components/Kanban/KanbanCard";
import CreateCard from "../CreateCard";
import MobileTodo from "./MobileTodo";
import { TooltipName } from "../../../../../utils/Enums";

const MobileKanbanBoard = ({ workflowSteps, projects, boardName, id }) => {
  const boardRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [maxHeight, setMaxHeight] = useState(0);
  const [isTeam, setIsTeam] = useState(searchParams.get("switch") === "true");
  const [activeSection, setActiveSection] = useState(null);
  const [milestone] = useCustomSearchParams("milestone");

  const priorityOrder = {
    Critical: 1,
    High: 2,
    Medium: 3,
    Low: 4,
  };

  useEffect(() => {
    const switchParam = searchParams.get("switch");
    setIsTeam(switchParam === "true");
  }, [searchParams]);

  const handleOnDrag = (e) => {
    const board = boardRef.current;
    if (!board) return;

    const { clientY } = e;
    const buffer = 50;
    const scrollSpeed = 25;

    if (clientY - board.offsetTop < buffer) {
      board.scrollBy({ top: -scrollSpeed, behavior: "smooth" });
    } else if (board.offsetTop + board.clientHeight - clientY < buffer) {
      board.scrollBy({ top: scrollSpeed, behavior: "smooth" });
    }
  };

  const groupedCardByWorkFlowSteps = projects.reduce((acc, item) => {
    const stepId = item?.card?.workflow_step_id;
    if (!acc[stepId]) {
      acc[stepId] = [];
    }
    acc[stepId].push(item);
    return acc;
  }, {});

  useEffect(() => {
    const calculateMaxHeight = Object.values(groupedCardByWorkFlowSteps)?.map(
      (cardGroup) => (Array.isArray(cardGroup) ? cardGroup.length : 0)
    );

    const tallestHeight = Math.max(...calculateMaxHeight);
    setMaxHeight(tallestHeight);
  }, [groupedCardByWorkFlowSteps]);

  const sortByPriority = (projects) => {
    return projects?.sort(
      (a, b) =>
        priorityOrder[a?.card?.priority] - priorityOrder[b?.card?.priority]
    );
  };

  const todoProjects =
    sortByPriority(projects.filter((card) => card.card.is_todo)) || [];

  const preventing = (e) => {
    e.preventDefault();
  };

  return (
    <div ref={boardRef} className="mobile-kanban" onDragOver={handleOnDrag}>
      <AccordionSection
        title="Quick Tasks"
        count={todoProjects.length}
        isOpen={activeSection === "quick-tasks"}
        onToggle={() =>
          setActiveSection(
            activeSection === "quick-tasks" ? null : "quick-tasks"
          )
        }
      >
        <MobileTodo
          projects={todoProjects}
          height={221}
          maxHeight={isTeam ? maxHeight : ""}
          boardName={boardName}
        />
      </AccordionSection>

      {/* Dynamic Workflow Steps Accordions */}
      {workflowSteps.map((column) => {
        const filteredProjects = sortByPriority(
          groupedCardByWorkFlowSteps[column?.workflow_step_id] || []
        );

        return (
          <AccordionSection
            key={column.workflow_step_id}
            title={column.status}
            count={filteredProjects.length}
            isOpen={activeSection === column.workflow_step_id}
            onToggle={() =>
              setActiveSection(
                activeSection === column.workflow_step_id
                  ? null
                  : column.workflow_step_id
              )
            }
            onDragOver={preventing}
          >
            <Droppable droppableId={column.workflow_step_id.toString()}>
              {(provided) => (
                <div
                  className="mobile-kanban-column"
                  style={{
                    maxHeight: `calc(94vh - ${milestone ? "340px" : "270px"})`,
                    overflowY: "auto",
                  }}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <CreateCard stage={column.workflow_step_id} boardId={id} />
                  {filteredProjects.map((project, index) => (
                    <KanbanCard
                      key={`${project?.card?.card_id}`}
                      id={project?.card?.card_id}
                      project={project}
                      boardName={boardName}
                      index={index}
                      stage={column.workflow_step_id}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </AccordionSection>
        );
      })}
    </div>
  );
};

const AccordionSection = ({
  title,
  count,
  isOpen,
  onToggle,
  children,
  onDragOver,
}) => {
  const [mouseIsHovering, setMouseIsHovering] = useState(false);

  return (
    <div
      className={`accordion-section ${
        mouseIsHovering ? "kanban-column-bg-1" : "kanban-column-bg-2"
      }`}
      onDragEnter={() => setMouseIsHovering(true)}
      onDragLeave={() => setMouseIsHovering(false)}
      onDragOver={onDragOver}
    >
      <div className="accordion-header" onClick={onToggle}>
        <div className="header-content">
          <h4 className="coloumn-header-h4">
            <p className="coloumn-header-text">{title}</p>
            <Tooltip title={TooltipName.TICKETS_IN_COLUMN}>
              <span className="coloumn-header-length">{count}</span>
            </Tooltip>
          </h4>
        </div>
        <button className={`expand-button ${isOpen ? "open" : ""}`}>
          {isOpen ? "▼" : "▶"}
        </button>
      </div>
      {isOpen && <div className="accordion-content">{children}</div>}
    </div>
  );
};

export default React.memo(MobileKanbanBoard);
